import { Utility } from '@app/models/utility';
import { Define } from './define';
import type { DocumentData, DocumentReference } from 'firebase/firestore';
import { getDoc } from 'firebase/firestore';
import { RequestRecord } from './request-record';

export enum CellState {
    Invalid = 0,
    Normal,
    Requesting,
    Approved,
    Rejected,
}
export class TimeStamp {
    stamp: Date;
    kind: number;
    constructor() {
    }
}

export class ReportCell {
    text: string;
    csvText: string;
    color: string;
    state: CellState;
    noData = '-';
    sourceDate: Date[];
    rawDate: Date[];
    hasRequest: boolean;
    constructor() {
        this.text = this.noData;
        this.color = '';
        this.state = CellState.Invalid;
        this.sourceDate = new Array<Date>();
        this.rawDate = new Array<Date>();
    }
    setState(state: CellState) {
        this.state = state;
    }
    getColor() {
        switch (this.state) {
            case CellState.Invalid:
                return 'warn';
            case CellState.Normal:
                return '';
            case CellState.Requesting:
                return 'accent';
            case CellState.Approved:
                return 'primary';
        }
    }
    toString(): string {
        switch (this.state) {
            case CellState.Normal:
            case CellState.Approved:
            case CellState.Requesting:
            case CellState.Rejected:
                return this.text;
            case CellState.Invalid:
                return this.noData;
            default:
                return '';
        }
    }
    getCsvString(target: Date, withoutDQuote: boolean = false): string {
        switch (this.state) {
            case CellState.Normal:
            case CellState.Approved:
            case CellState.Requesting:
            case CellState.Rejected:
                const dateDiff = ['前', '', '翌'];
                const changeText = this.hasRequest ? `\n変更\n${this.csvText}` : '';
                if (withoutDQuote) {
                  return `${this.sourceDate.map(x => {
                    const dateDiffIndex = Utility.diffDay(target, x) + 1;
                    return dateDiff[dateDiffIndex] + Utility.convertDateToMinite(x);
                  }).join('\n')}${changeText}`;
                } else {
                  return `"${this.sourceDate.map(x => {
                    const dateDiffIndex = Utility.diffDay(target, x) + 1;
                    return dateDiff[dateDiffIndex] + Utility.convertDateToMinite(x);
                  }).join('\n')}${changeText}"`;
                }
            case CellState.Invalid:
                return this.noData;
            default:
                return '';
        }
    }
    empty(): boolean {
        return this.sourceDate.length === 0;
    }
    setDate(date: Date, state: CellState = CellState.Normal, target?: Date) {
        if ( date === undefined ) {
            return;
        }
        if (('0' + date.getMinutes() === undefined)) {
            console.log(`setDate illegal data ${date}`);
        }
        const dateDiff = ['前', '', '翌'];
        const dateDiffIndex = Utility.diffDay(target,date) + 1;
        this.text = dateDiff[dateDiffIndex] + Utility.convertDateToMinite(date);
        this.state = state;
        this.sourceDate.length = 0;
        this.sourceDate.push(date);
        this.rawDate.push(date);
        this.csvText = this.text;
    }
    addDate(date: Date, state: CellState = CellState.Normal, target?: Date) {
        if ( date === undefined ) {
            return;
        }
        // 5つ目以降の打刻の取消は出来ないがとりあえず追加する。
        // if ( this.sourceDate.length >= 4 ) {
        //     // 5つ目以降の打刻は無視する。（外出、戻りの上限）
        //     return;
        // }
        if (('0' + date.getMinutes() === undefined)) {
            console.log(`setDate illegal data ${date}`);
        }
        const dateDiff = ['前', '', '翌'];
        const dateDiffIndex = Utility.diffDay(target,date) + 1;
        this.text += '\n' + dateDiff[dateDiffIndex] + Utility.convertDateToMinite(date);
        this.state = state;
        this.sourceDate.push(date);
        this.rawDate.push(date);
        this.csvText = this.text;
        // console.log('addDate');
        // console.log(this.sourceDate);
    }
    refresh(target: Date) {
        // console.log('refresh');
        // console.log(this.sourceDate);
        this.sourceDate = this.sourceDate.filter( x => x !== null);
        // console.log(this.sourceDate);
        // 実打刻と申請をマージした際にソートが必要となる
        this.sourceDate.sort((a, b) => a.valueOf() - b.valueOf());

        const dateDiff = ['前', '', '翌'];
        if (this.sourceDate.length !== 0) {
          this.text = '';
          this.sourceDate.forEach( date => {
          const dateDiffIndex = Utility.diffDay(target,date) + 1;
            this.text += dateDiff[dateDiffIndex] + Utility.convertDateToMinite(date) + '\n';
          });
        } else {
          this.text = '-';
        }

        this.csvText = this.text;
        // console.log(`refresh ${this.text}`);
    }
    clear() {
        this.text = this.noData;
        this.state = CellState.Invalid;
        this.sourceDate.length = 0;
        this.hasRequest = false;
    }
    copy(cell: ReportCell) {
        this.text = cell.text;
        this.state = cell.state;
        this.sourceDate = cell.sourceDate;
        this.hasRequest = cell.hasRequest;
    }
}

export class ReportRecord {
    dateText: string;
    dateTextShort: string;
    sourceStamps: TimeStamp[];
    request: RequestRecord;
    state: number;
    cells: ReportCell[];
    approverName: string;
    constructor(public date: Date) {
        const dayOfWeek = '（' + ['日', '月', '火', '水', '木', '金', '土'][this.date.getDay()] + '）';
        this.dateText = this.date.toLocaleDateString() + dayOfWeek;
        this.dateTextShort = this.date.getDate() + dayOfWeek; // 月を表示つする窮屈、表示しないとスマートだがユーザーに無意識のストレスを溜める可能性
        this.sourceStamps = [];
        this.cells = new Array<ReportCell>(4);
        for (let i = 0; i < this.cells.length; i++) {
            this.cells[i] = new ReportCell();
        }
        this.state = -1;
    }
    register(timeStamp: Date, target: Date, kind: number, _placeId: string) {
        // 無理やり7/4以前の打刻は自動振り分けにする。端末のVer1.0.0.2でkind=10になっていたため。
        // 2019/9月以降ならこの条件は消して良い。
        const isForceAutomationFlag = timeStamp < new Date('2019/07/05 05:00:00');
        if (kind <= 1 || isForceAutomationFlag) {
            // 打刻回数によって振り分ける場合はこれ。
            this.registerAutomation(timeStamp, target);
        } else {
            // 打刻種別によって振り分ける場合はこれ。
            const index = kind % 10;
            // console.log('register ' + timeStamp.toLocaleString() + ' ' + index.toString());

            switch (index) {
                case 0:
                    // 出社は、時系列ソートしたtimeStampを入力する前提で、最早打刻を採用する。
                    if (this.cells[index].empty()) {
                        this.cells[index].setDate(timeStamp, CellState.Normal, target);
                    }
                    break;
                case 1:
                    // 退社は、最遅打刻を採用する。
                    this.cells[index].setDate(timeStamp, CellState.Normal, target);
                    break;
                case 2:
                    // 外出・戻りは4組までの仕様だが、5組以上打刻された時の動作が未定義である。問い合わせ中。
                    // とりあえず、全部追加する。
                    if (!this.cells[index].empty() && this.cells[index]) {
                        this.cells[index].addDate(timeStamp, CellState.Normal, target);
                    } else {
                        this.cells[index].setDate(timeStamp, CellState.Normal, target);
                    }
                    break;
                case 3:
                    // 外出・戻りは4組までの仕様だが、5組以上打刻された時の動作が未定義である。問い合わせ中。
                    // とりあえず、全部追加する。
                    if (!this.cells[index].empty()) {
                        this.cells[index].addDate(timeStamp, CellState.Normal, target);
                    } else {
                        this.cells[index].setDate(timeStamp, CellState.Normal, target);
                    }
                    break;
            }
        }
        const t = new TimeStamp();
        t.stamp = timeStamp;
        t.kind = kind % 10;
        this.sourceStamps.push(t);
    }
    registerAutomation(timeStamp: Date, target: Date, state: CellState = CellState.Normal) {
        // console.log(`registerAutomation ${timeStamp}`);
        if (this.cells[0].empty()) {
            // 先頭が空なら出勤とする。
            this.cells[0].setDate(timeStamp, state, target);
            return;
        }
        if (this.cells[1].empty() &&
            this.cells[2].empty() &&
            this.cells[3].empty()) {
            // 退勤以降が全部空ならとりあえず退勤扱いにする。
            this.cells[1].setDate(timeStamp, state, target);
            return;
        }
        if (this.cells[2].empty()) {
            // 出勤・退勤が埋まっていれば、退勤にあるものは外出、今回のデータは戻りにする。退勤はクリアしておく。
            this.cells[3].setDate(timeStamp, state, target);
            this.cells[2].copy(this.cells[1]);
            this.cells[1].clear();
            return;
        }
        if (!this.cells[3].empty() &&
            this.cells[1].empty()) {
            // 戻りが埋まっていて退勤が空なら、退勤扱いにする。
            this.cells[1].setDate(timeStamp, state, target);
            return;
        }
        // 5回目以降の打刻は、最終打刻を退社に、1つ前を戻りにずらす。
        this.cells[3].copy(this.cells[1]);
        this.cells[1].setDate(timeStamp, state, target);
    }
    registerRequest(request: RequestRecord, target: Date , approverName: string) {
        // createdが無いレコード対策で、既に登録済なら無視する。
        if (this.request !== undefined) {
            if (request.created === undefined) {
                return;
            }
            if (this.request.created > request.created) {
                return;
            }
        }

        // 承認済み後、同日の新しい申請が却下された場合。承認済みの申請が適用されなくなるが、メールで却下通知を受け取れないユーザーもいるためしっかりとstateとして表示すべき。
        this.state = request.state;
        this.request = request;
        this.approverName = approverName;
        // 却下された申請は無視する。
        if (request.state === Define.RequestState.Reject) {
            return;
        }
        // console.log('register request.');
        // console.log(request);
        // dataVersion = 2に対応した上書き処理を行う。
        let state;
        switch ( request.state  ) {
            case  Define.RequestState.Registered:
                state = CellState.Requesting;
                break;
            case Define.RequestState.Approve:
                state = CellState.Approved;
                break;
            case Define.RequestState.Reject:
                state = CellState.Rejected;
                break;
            default:
                state = CellState.Invalid;
                break;
        }

        // dataVersion2と3でchangeStampsの仕様が変わる  それぞれの意味合い[2:上書き変更 3:新規追加]
        if (request.dataVersion === 2) {
          request.changeStamps.forEach( (c, index) => {
              const isEnable = c !== null && c !== undefined;
              // 変更日時が存在した場合
              if ( index >= 0 && index <= 1 ) {
                // 出社・退社
                if ( request.ejectFlags[index] ) {
                    // 取消なら削除する。
                    this.cells[index].clear();
                } else if (isEnable) {
                    // 取消でなければ、上書きで変更する。
                    // TODO なぜか、コメントアウトしている実装だと表示が反映されない。setDateと何が違うのか。。。。
                    // とりあえず、setDateしてから生の打刻配列から終端を削って帳尻合わせしている。
                    // 修正は必要。
                    // this.cells[index].sourceDate.length = 0;
                    // this.cells[index].sourceDate.push( c.toDate() );
                    // this.cells[index].text = Utility.convertDateToMinite(c.toDate());
                    // this.cells[index].csvText = this.cells[index].text;
                    this.cells[index].setDate(c.toDate() , state, target);
                    this.cells[index].rawDate.splice(this.cells[index].rawDate.length - 1);
                }
                this.cells[index].hasRequest = true;
              } else if ( index % 2 === 0) {
                // 外出
                if ( request.ejectFlags[index] ) {
                  // 取消なら当該データを削除する。
                  this.cells[2].sourceDate[(index - 2) / 2] = null;
                } else if (isEnable) {
                  // 取消でなければ、上書きで変更する。
                  this.cells[2].sourceDate[(index - 2) / 2] = c.toDate();
                  this.cells[2].setState(state);
                }
                this.cells[2].hasRequest = true;
              } else if ( index % 2 === 1) {
                // 戻り
                if ( request.ejectFlags[index] ) {
                  // 取消なら当該データを削除する。
                  this.cells[3].sourceDate[(index - 3) / 2] = null;
                } else if (isEnable) {
                  // 取消でなければ、上書きで変更する。
                  this.cells[3].sourceDate[(index - 3) / 2] = c.toDate();
                  this.cells[3].setState(state);
                }
                this.cells[3].hasRequest = true;
              }
          });
        } else if (request.dataVersion === 3) {
          // 実打刻の取消処理
          request.ejectFlags.forEach( (e, index) => {
            if (index >= 0 && index <= 1) {
              // 出社・退社
              // 出社・退社時の実打刻を取り消す状況はあるのか？
            } else if (index % 2 === 0) {
              // 外出
              if (e) {
                this.cells[2].sourceDate = this.cells[2].sourceDate.filter(stamp =>
                  Utility.convertDateToDateMinite(stamp) !== request.actualStamps[index]);
              }
            } else if ( index % 2 === 1) {
              // 戻り
              if (e) {
                this.cells[3].sourceDate = this.cells[2].sourceDate.filter(stamp =>
                  Utility.convertDateToDateMinite(stamp) !== request.actualStamps[index]);
              }
            }
          });
          request.changeStamps.forEach( (c, index) => {
              const isEnable = c !== null && c !== undefined;
              // 変更日時が存在した場合
              if ( index >= 0 && index <= 1 ) {
                // 出社・退社
                if ( request.ejectFlags[index] ) {
                    // 取消なら削除する。
                    this.cells[index].clear();
                } else if (isEnable) {
                    // 取消でなければ、上書きで変更する。
                    // TODO なぜか、コメントアウトしている実装だと表示が反映されない。setDateと何が違うのか。。。。
                    // とりあえず、setDateしてから生の打刻配列から終端を削って帳尻合わせしている。
                    // 修正は必要。
                    // this.cells[index].sourceDate.length = 0;
                    // this.cells[index].sourceDate.push( c.toDate() );
                    // this.cells[index].text = Utility.convertDateToMinite(c.toDate());
                    // this.cells[index].csvText = this.cells[index].text;
                    // firestoreのchangeStampには日本時間に合わせることで現地時間となるtimestampを記録している。
                    this.cells[index].setDate(Utility.convertLocalTimeToJstDate(c.toDate()) , state, target);
                    this.cells[index].rawDate.splice(this.cells[index].rawDate.length - 1);
                }
                this.cells[index].hasRequest = true;
              } else if ( index % 2 === 0) {
                // 外出
                if (isEnable) {
                  if (this.cells[2].empty()) {
                    this.cells[2].sourceDate.length = 0;
                    // firestoreのchangeStampには日本時間に合わせることで現地時間となるtimestampを記録している。
                    this.cells[2].sourceDate.push(Utility.convertLocalTimeToJstDate(c.toDate()));
                  } else {
                    // firestoreのchangeStampには日本時間に合わせることで現地時間となるtimestampを記録している。
                    this.cells[2].sourceDate.push(Utility.convertLocalTimeToJstDate(c.toDate()));
                  }
                  this.cells[2].setState(state);
                }
                this.cells[2].hasRequest = true;
              } else if ( index % 2 === 1) {
                // 戻り
                if (isEnable) {
                  if (this.cells[3].empty()) {
                    this.cells[3].sourceDate.length = 0;
                    // firestoreのchangeStampには日本時間に合わせることで現地時間となるtimestampを記録している。
                    this.cells[3].sourceDate.push(Utility.convertLocalTimeToJstDate(c.toDate()));
                  } else {
                    // firestoreのchangeStampには日本時間に合わせることで現地時間となるtimestampを記録している。
                    this.cells[3].sourceDate.push(Utility.convertLocalTimeToJstDate(c.toDate()));
                  }
                  this.cells[3].setState(state);
                }
                this.cells[3].hasRequest = true;
              }
          });
        }
        this.cells.forEach( c => c.refresh(target));

        // // 一旦打刻登録表示をクリアする。
        // for (let i = 0; i < this.cells.length; i++) {
        //     this.cells[i].clear();
        // }
        // // 実打刻と登録データのマージ用リスト
        // const mergedStamps = new Array();
        // // 申請作成タイミングより後の当該営業日打刻は、そのまま採用する。
        // if (request.created !== undefined) {
        //     const registerdDate = request.created.toDate();
        //     this.sourceStamps.filter( elm => elm.stamp > registerdDate).forEach( elm => {
        //         mergedStamps.push({stamp: elm, state: CellState.Normal});
        //     });
        // }

        // // このクラスは1営業日を表す。
        // // RequestRecordも1日分なので、申請レコードで完全に上書きする思想としている。
        // // Cell単位で、リクエストの日時、分まで一致していれば同じとみなして、異なれば変更or追加として扱う。
        // // 変更or追加でstateを見て、申請中、承認済を分ける。
        // // 取消した時の扱いがどうやっても解決できないので、申請内容を別途ダイアログで見られるようにしている。
        // request.timeStamps.forEach( timeStamp => {
        //     const date = timeStamp.toDate();
        //     let state = CellState.Normal;
        //     const findResult = this.sourceStamps.find( (t) => {
        //         return (date.getFullYear() === t.stamp.getFullYear() &&
        //             date.getMonth() === t.stamp.getMonth() &&
        //             date.getDate() === t.stamp.getDate() &&
        //             date.getDay() === t.stamp.getDay() &&
        //             date.getHours() === t.stamp.getHours() &&
        //             date.getMinutes() === t.stamp.getMinutes() );
        //     });
        //     if (findResult !== undefined) {
        //         // 年月日時分が一致していれば、変更無し。
        //     } else {
        //         // 不一致なら申請中または承認済。
        //         if (request.state === Define.RequestState.Registered) {
        //             state = CellState.Requesting;
        //         } else if (request.state === Define.RequestState.Approve) {
        //             state = CellState.Approved;
        //         } else if (request.state === Define.RequestState.Reject) {
        //             state = CellState.Rejected;
        //         }
        //     }
        //     mergedStamps.push({stamp: date, state: state});
        // });
        // this.state = request.state;
        // // 実打刻→申請データの順で登録しているため、前後関係が実打刻>申請データの関係になった場合に順序が入れ替わる。
        // // 一旦マージ用配列で受けてからソートする。
        // mergedStamps.sort( (a, b) => {
        //     if (a.stamp > b.stamp) {
        //         return 1;
        //     }
        //     if (a.stamp < b.stamp) {
        //         return -1;
        //     }
        //     return 0;
        // });
        // mergedStamps.forEach( elm => {
        //     this.registerAutomation(elm.stamp, elm.state);
        // });

    }

    getState(withNewLine: boolean = false): string {
        switch (this.state) {
            case Define.RequestState.Approve:
                return '承認済' + (withNewLine ? '\n' : '') + '（' + this.approverName + '）';
            case Define.RequestState.Registered:
                return '申請中' + (withNewLine ? '\n' : '') + '（' + this.approverName + '）';
            case Define.RequestState.Reject:
                return '却下' + (withNewLine ? '\n' : '') + '（' + this.approverName + '）';
            default:
                return '';
        }
    }

    toString(): string {
        return `${this.date.toLocaleDateString()},${this.cells.join(',')},${this.getState()}`;
    }

    isHoliday(): boolean {
        return this.date.getDay() === 0 || this.date.getDay() === 6;
    }
    getColor() {
        switch (this.state) {
            case Define.RequestState.Registered:
                return 'warn';
            case Define.RequestState.Reject:
                return 'accent';
            case Define.RequestState.Approve:
                return 'primary';
            default:
                return '';
        }
    }
    getCsvString() {
        const times = new Array();
        this.cells.forEach( c => times.push(c.getCsvString(this.date)));
        const cause = this.request !== null && this.request !== undefined ? this.request.cause : '';
        return `${this.dateText},${times.join(',')},${this.getState()},${cause}`;
    }
    getHTMLString(): [string, number] {
      let dateTextStyle = '\"font-size:10pt;width:13%;margin:auto;text-align:left;border: 1px solid; white-space: pre-line;\"';
      const stateStyle = '\"width:20%;margin:auto;text-align:left;border: 1px solid; white-space: pre-line;\"';
      // 申請理由で【white-space: pre-line;】は全体のフォントサイズ縮小の原因になるので見辛くなるが止めておく
      const causeStyle = '\"width:30%;margin:auto;text-align:left;border: 1px solid; white-space: pre-line;\"';
      const times = new Array();
      this.cells.forEach( c => times.push(c.getCsvString(this.date, true)));
      const cause = this.request !== null && this.request !== undefined ? this.request.cause : '';
      let tableRecordHTML = '<tr>';

      const dayOfWeek = '(' + ['日', '月', '火', '水', '木', '金', '土'][this.date.getDay()] + ')';

      if (this.date.getDay() === 0)
      {
        // 日曜
        dateTextStyle = '\"font-size:10pt;width:13%;margin:auto;text-align:left;border: 1px solid; white-space: pre-line; color: #ff0000;\"';
      }
      else if (this.date.getDay() === 6)
      {
        // 土曜
        dateTextStyle = '\"font-size:10pt;width:13%;margin:auto;text-align:left;border: 1px solid; white-space: pre-line; color: #0000ff;\"';
      }

      tableRecordHTML += `<td style=${dateTextStyle}>${this.date.getDate() + '日' + dayOfWeek}</td>`;
      let textHeight = 1;
      let max = 1;
      times.forEach(timeStr => {
        textHeight = timeStr.split('\n').length;
        const timeStyle = `\"width:10%;margin:auto;text-align:left;border: 1px solid; white-space: pre-line;\"`;
        tableRecordHTML += `<td style=${timeStyle}>${timeStr}</td>`;

        if (max < textHeight) {
          max = textHeight;
        }
      });

      const state = this.getState(true);
      textHeight = state.split('\n').length;
      if (max < textHeight) {
        max = textHeight;
      }
      tableRecordHTML += `<td style=${stateStyle}>${state}</td>`;

      // 13文字で改行される
      textHeight = Math.floor(cause.length / 13);
      if (max < textHeight) {
        max = textHeight;
      }
      tableRecordHTML += `<td style=${causeStyle}>${cause}</td>`;
      tableRecordHTML += '</tr>';

      return [tableRecordHTML, max];
    }
}

export class Report {
    public data: ReportRecord[] = [];

    constructor(
        public startDate: Date,
        public endDate: Date,
        private userName: string,
        private userId: string,
        private team: DocumentReference<DocumentData>
    ) {
        // ふりわけ変更影響分レコードを増やす。
        const index = new Date(this.startDate);
        index.setDate(index.getDate() - 2);
        console.log('start %s', startDate);
        console.log('end %s', endDate);
        const end = new Date(this.endDate);
        end.setDate(end.getDate() + 2);
        while (index <= end) {
            try {
                const r: ReportRecord = new ReportRecord(new Date(index));
                this.data.push(r);
            } catch (err) {
                console.log('create records2-1 err:%s index:%s', err, index);
            }
            index.setDate(index.getDate() + 1);
        }
    }

    register(dateText: string, kind: number, _placeId: string, target: string) {
        // 営業日を判定する。
        const inDate: Date = Utility.createDateFromText(dateText);
        try {
            if (target === undefined) {
              // ふりわけ変更がされていないなら
              if (this.isTheDay(dateText) === false) {
                // 0時~5時なら1日前の打刻として登録
                const a: Date = new Date(inDate);
                a.setDate(inDate.getDate() - 1);
                if (a.getTime() < this.startDate.getTime()) {
                    console.log('report register error');
                    return;
                }
                console.log(inDate.toString());
                const record = this.data.find(r => this.equalDate(a, r.date));

                if (record === undefined) {
                    console.log('missing date %s', dateText);
                    return;
                }
                // const registerDate = new Date(inDate);
                // registerDate.setDate(inDate.getDate() - 1);
                record.register(inDate, a , kind, _placeId);
              } else {
                this.data.find(r => this.equalDate(r.date, inDate)).register(new Date(inDate), inDate , kind, _placeId);
              }
            } else {
              // ふりわけ変更がされているなら
              const targetDate: Date = Utility.createDateFromText(target);
              // ふりわけのの影響で配列外を読まないようにする。
              if (Utility.compareDay(this.startDate, targetDate) >= 0 && Utility.compareDay(this.endDate, targetDate) >= -1) {
                const record = this.data.find(r => this.equalDate(r.date, targetDate));
                if (record === undefined) {
                  console.log('missing target date %s', targetDate);
                  return;
                }
                record.register(new Date(inDate), targetDate , kind, _placeId);
              }
            }

        } catch (err) {
            console.log('report register err date:%s messege:%s', dateText, err);
        }
    }

    registerRequest(request: RequestRecord, approverName: string) {
      // firestoreのtargetには日本時間に合わせることで現地時間となるtimestampを記録している。
        const inDate: Date = Utility.convertLocalTimeToJstDate(request.target.toDate());
        try {
            const record = this.data.find(r => this.equalDate(inDate, r.date));
            if (record !== undefined) {
                // 申請時の引数targetは申請対象日と同じなのでinDateを渡す
                record.registerRequest(request, inDate , approverName);
            } else {
                console.log('not found %s', inDate);
            }
        } catch (err) {
            console.log('request register timeStamp:%s err:%s', inDate, err);
        }
    }

    isTheDay(date: string): boolean {
        if (date === undefined) {
            console.log('Report isTheDay undefined date %s', date);
            return false;
        }
        if (date.length < 13) {
            console.log('Report isTheDay illegal date %s', date);
            return false;
        }
        const hour = Number(date.slice(11, 13));
        // 0～5時までなら前日の打刻扱いとする。（最終的には、打刻機によって変えるべき判定基準である）
        if (hour < 5) {
            return false;
        } else {
            return true;
        }
    }

    createCsv() {
        return new Promise<string>((resolve: (value?: string) => void, reject: (reason?: any) => void) => {
            getDoc(this.team).then(doc => {
                const teamName = doc.data()['name'];
                const title = `社員番号,${this.userId},,,\r\n氏名,${this.userName},,,\r\n所属部署,${teamName},,,\r\n`;
                const term = `期間,${this.startDate.toLocaleDateString()}～${this.endDate.toLocaleDateString()},,,\r\n`;
                const header = ['日付,出社,退社,外出,戻り,承認状態,理由\r\n'];
                const body = new Array();
                // ふりわけ変更での配列外参照用の2日分のデータを無視
                for (let i = 2 ; i <= this.data.length - 3; i++) {
                  body.push(this.data[i].getCsvString());
                }
                resolve(title + term + header + body.join('\r\n'));
            }).catch(_err => { reject('error csv'); });
        });
    }

    createHTMLforPDF() {
      return new Promise<[string, number]>((resolve: (value?: [string, number]) => void, reject: (reason?: any) => void) => {
        getDoc(this.team).then(doc => {
          const teamName = doc.data()['name'];
          let lineNumber = 5; // テーブルヘッダーと社員情報で5行分
          const body = new Array();
          // ふりわけ変更での配列外参照用の2日分のデータを無視
          for (let i = 2 ; i <= this.data.length - 3; i++) {
            let maxTextHeight = 1;
            let tableRecordHTML = '';

            [tableRecordHTML, maxTextHeight] = this.data[i].getHTMLString();
            body.push(tableRecordHTML);
            lineNumber += maxTextHeight;
          }

          const tableHeaderStyle = '\"font-size:10pt;text-align:left;background-color: white; border: 0px;\"';
          const tableDataStyle = '\"font-size:10pt;text-align:right;border: 0px\"';

          let html = `<div style="width:594.35pt;">
          <table style="border: 0px;">`;

          html += `<tr><th style=${tableHeaderStyle}>社員番号</th>`;
          html += `<td style=${tableDataStyle}">${this.userId}</td></tr>`;

          html += `<tr><th style=${tableHeaderStyle}>氏名</th>`;
          html += `<td style=${tableDataStyle}">${this.userName}</td></tr>`;

          html += `<tr><th style=${tableHeaderStyle}>所属部署</th>`;
          html += `<td style=${tableDataStyle}">${teamName}</td></tr>`;

          html += `<tr><th style=${tableHeaderStyle}>期間</th>`;
          html += `<td style=${tableDataStyle}">${this.startDate.toLocaleDateString()}～${this.endDate.toLocaleDateString()}</td></tr>`;
          html += `</table>`;

          html += `
            <table style="width:594.35pt;border: 1px solid;margin:0;padding:0;">
            <thead style="width:100%;">
            <tr style="width:100%;">
            <th style="font-size:10pt;width:10%;text-align:left;background-color: white; border: 1px solid;">日付</th>
            <th style="font-size:10pt;width:10%;text-align:left;background-color: white; border: 1px solid;">出社</th>
            <th style="font-size:10pt;width:10%;text-align:left;background-color: white; border: 1px solid;">退社</th>
            <th style="font-size:10pt;width:10%;text-align:left;background-color: white; border: 1px solid;">外出</th>
            <th style="font-size:10pt;width:10%;text-align:left;background-color: white; border: 1px solid;">戻り</th>
            <th style="font-size:10pt;width:20%;text-align:left;background-color: white; border: 1px solid;">承認状態</th>
            <th style="font-size:10pt;width:30%;text-align:left;background-color: white; border: 1px solid;">理由</th>
            </tr>
            </thead>
          `;

          html += '<tbody>' + body.join('') + '</tbody>';

          html += `
          </table>
          <div style="margin-top:10px;text-align: right;">
          ${this.userId}_${this.userName}
          </div>
          </div>
          `;

          resolve([html, lineNumber]);
        }).catch(_err => { reject('error create html string'); });
      });
    }

    equalDate(a: Date, b: Date): boolean {
        return (
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate()
        );
    }

}
